/**
 * Credit-product classification + monthly loan analysis (#131 Phase 2).
 *
 * Melissa's Cash App export blends several DISTINCT credit products under vague
 * labels. The old loans page summed them all as one "Cash App Borrow" total and
 * produced garbage ($47k repaid, $31k "fees"). This module separates them by their
 * VERIFIED CSV signatures so each is summarized correctly.
 *
 * Verified against the real 9,333-row export (and reconciled to the live DB):
 *   - Cash App Borrow: note "Borrowing in Cash App" — 926 rows.
 *       borrowed $16,773.90, repaid $23,967.94, COST $7,194.04 (= repaid-borrowed).
 *       That cost = ~$839 base 5% fee + ~$6,355 overdue interest (1.25%/wk late).
 *   - Overdraft: Transaction Type "Overdraft" — fee-free coverage, ~$0 real cost.
 *   - Afterpay (BNPL): note "Afterpay" — cost NOT provable from the export.
 *   - Weokie: name/notes contain "WEOKIE" — an EXTERNAL MORTGAGE paid through Cash
 *       App (~$24,384 across the file). A separate loan; principal/interest can't
 *       be split without the rate, so we report total-paid only.
 *
 * The fee column is $0.00 on every credit-product row — Cash App never itemizes
 * cost — so Borrow cost is derived as repaid - borrowed (the bulletproof identity).
 */

export type CreditProduct =
  | "cashapp-borrow"
  | "overdraft"
  | "afterpay"
  | "weokie-mortgage"
  | "other";

// Minimal shape we need off a Transaction — works for both the app's Transaction
// type and a raw normalized row.
export interface CreditRow {
  name?: string | null;
  note?: string | null;
  notes?: string | null;
  csvType?: string | null;
  transactionType?: string | null;
  type: string;        // "credit" | "debit"
  amount: number;      // stored as ABS; sign comes from `type`
  date: string;
}

function text(...vals: (string | null | undefined)[]): string {
  return vals.map((v) => (v || "").toLowerCase()).join(" ");
}

/** Classify a row into its credit product by verified CSV signature. */
export function classifyCreditProduct(r: CreditRow): CreditProduct {
  // The Cash App descriptor ("Borrowing in Cash App", "Afterpay") lands in EITHER
  // name OR note depending on the import path — the stored DB rows carry it in
  // `name` (note is empty), while a fresh normalize may put it in note. Search
  // both so detection works regardless. (bugfix: was note-only → matched $0)
  const descText = text(r.name, r.note, r.notes);
  const typeText = text(r.csvType, r.transactionType);
  const nameOnly = (r.name || "").toLowerCase().trim();

  if (descText.includes("weokie")) return "weokie-mortgage";
  // Cash App Borrow by name/note ("Borrowing in Cash App", how the DB stores it)
  // OR by the raw Transaction Type "Borrow" (present on fresh imports before the
  // type gets dropped). Both paths must work. (#131)
  if (descText.includes("borrowing in cash app") || typeText.includes("borrow"))
    return "cashapp-borrow";
  // Overdraft: the raw Transaction Type "Overdraft" was dropped on import (csvType
  // is empty in the DB), so detect by the exact stored names Cash App uses for
  // overdraft entries — "repayment" / "non-repayment update" — or the type if present.
  if (
    typeText.includes("overdraft") ||
    nameOnly === "repayment" ||
    nameOnly === "non-repayment update"
  )
    return "overdraft";
  if (descText.includes("afterpay")) return "afterpay";
  return "other";
}

/** True for Cash App Borrow rows (the fee-bearing short-term loan). */
export function isCashAppBorrow(r: CreditRow): boolean {
  return classifyCreditProduct(r) === "cashapp-borrow";
}

/** True for the Weokie mortgage (external loan paid through Cash App). */
export function isWeokieMortgage(r: CreditRow): boolean {
  return classifyCreditProduct(r) === "weokie-mortgage";
}

/**
 * THE single source of truth for "is this row LOAN PRINCIPAL that must be EXCLUDED
 * from income/expense totals." Everything that gates the money math (analytics,
 * dashboard, budget, subscriptions, the read-mapper) flows through here, so the
 * loans page and the rest of the app can never disagree about what's a loan.
 *
 * Currently this is **Cash App Borrow only** — and deliberately so:
 *  - Cash App Borrow: disbursement isn't income, principal repayment isn't spending
 *    (you're just returning borrowed cash). Excluded. ✅
 *  - A MORTGAGE payment (Weokie) is NOT excluded here — that money genuinely leaves
 *    for housing, so it counts as a real expense. Proper principal/interest split
 *    (treating principal as a transfer-to-equity) is the YNAB-grade feature tracked
 *    in the loan-liability-accounts epic (#141); until we have the rate, the whole
 *    mortgage payment stays counted as cash-flow.
 *  - Overdraft / Afterpay: cash-flow, shown on the loans page but NOT loan principal.
 *
 * Keeping this set = {cashapp-borrow} preserves today's verified math exactly while
 * unifying detection. Widen this set ONLY as part of #141, never silently.
 */
export function isLoanPrincipal(r: CreditRow): boolean {
  return classifyCreditProduct(r) === "cashapp-borrow";
}

/**
 * Signed amount: + for money IN (credit / disbursement), - for money OUT
 * (debit / repayment). Rows store amount as ABS and direction in `type`.
 */
export function signedAmount(r: CreditRow): number {
  return r.type === "credit" ? Math.abs(r.amount) : -Math.abs(r.amount);
}

export interface BorrowSummary {
  borrowed: number;   // total disbursed to you
  repaid: number;     // total paid back
  cost: number;       // repaid - borrowed (fees + overdue interest), never < 0
  baseFee: number;    // 5% of borrowed (the on-time portion)
  overdueInterest: number; // cost - baseFee (the late-carry portion)
  effectiveRate: number;   // cost / borrowed, e.g. 0.429
}

/** Summarize Cash App Borrow cost from its rows (pass ONLY borrow rows). */
export function summarizeCashAppBorrow(rows: CreditRow[]): BorrowSummary {
  let borrowed = 0;
  let repaid = 0;
  for (const r of rows) {
    const a = signedAmount(r);
    if (a > 0) borrowed += a;
    else if (a < 0) repaid += -a;
  }
  const cost = Math.max(repaid - borrowed, 0);
  const baseFee = borrowed * 0.05; // documented 5% flat finance charge
  const overdueInterest = Math.max(cost - baseFee, 0);
  const effectiveRate = borrowed > 0 ? cost / borrowed : 0;
  return { borrowed, repaid, cost, baseFee, overdueInterest, effectiveRate };
}

export interface CashFlowSummary {
  paidOut: number;   // total installments / clawbacks that left the account
  rows: number;      // how many transactions
}

/**
 * Summarize a CASH-FLOW credit product (Afterpay, Overdraft) — products whose
 * COST is NOT derivable from the Cash App export. We can only honestly report the
 * money that left the account, NOT how much of it was fee/interest:
 *  - Afterpay: the purchase is funded at the merchant (off-ledger), so "out" is
 *    installment repayment of spending we can't see — out-in is meaningless.
 *  - Overdraft: fee-free coverage; "out" repays overdrafted spend, $0 cost.
 * Returns money-out only, clearly labeled as cash-flow, never as "cost". (#131)
 */
export function summarizeCashFlow(rows: CreditRow[]): CashFlowSummary {
  let paidOut = 0;
  for (const r of rows) {
    const a = signedAmount(r);
    if (a < 0) paidOut += -a;
  }
  return { paidOut, rows: rows.length };
}

export interface MonthlyLoanRow {
  month: string;          // "YYYY-MM"
  income: number;         // real income that month (caller supplies)
  borrowTaken: number;    // Cash App Borrow disbursed
  borrowRepaid: number;   // Cash App Borrow repaid
  borrowCost: number;     // directional: repaid - taken this month (straddles months)
  mortgagePaid: number;   // Weokie payments (shown, but NOT in the % below)
  afterpayOut: number;    // Afterpay installments paid (cash-flow, cost not provable)
  overdraftOut: number;   // Overdraft clawbacks (cash-flow, $0 cost)
  owedEnd: number;        // running Cash App Borrow balance owed at END of month
                          // (cumulative borrowed − cumulative repaid, all-time to date)
  debtToIncomePct: number;// Cash App Borrow repaid / income. Mortgage is EXCLUDED —
                          // it's a mortgage, a different kind of debt, tracked separately.
}

function monthKey(date: string): string {
  return (date || "").slice(0, 7);
}

/**
 * Build the per-month loan table the Loans page renders. `incomeByMonth` is the
 * REAL income per month (transfers + loan principal already excluded upstream).
 * Cash App Borrow and the Weokie mortgage are kept in SEPARATE columns — never
 * summed into one "loan payments" number (that was the old page's bug).
 */
export function buildMonthlyLoanTable(
  rows: CreditRow[],
  incomeByMonth: Record<string, number>
): MonthlyLoanRow[] {
  type Bucket = { taken: number; repaid: number; mortgage: number; afterpay: number; overdraft: number };
  const m: Record<string, Bucket> = {};
  const ensure = (k: string): Bucket =>
    (m[k] ??= { taken: 0, repaid: 0, mortgage: 0, afterpay: 0, overdraft: 0 });

  for (const r of rows) {
    const k = monthKey(r.date);
    if (!k) continue;
    const product = classifyCreditProduct(r);
    const a = signedAmount(r);
    if (product === "cashapp-borrow") {
      if (a > 0) ensure(k).taken += a;
      else ensure(k).repaid += -a;
    } else if (product === "weokie-mortgage") {
      if (a < 0) ensure(k).mortgage += -a;
    } else if (product === "afterpay") {
      if (a < 0) ensure(k).afterpay += -a;
    } else if (product === "overdraft") {
      if (a < 0) ensure(k).overdraft += -a;
    }
  }

  // Include income-only months too, so the table spans the full history.
  const months = [...new Set<string>([...Object.keys(m), ...Object.keys(incomeByMonth)])];

  // Running Cash App Borrow balance owed, accumulated OLDEST→NEWEST, so each row
  // shows what was still owed at the end of that month (what "rolled over"). (#131)
  const owedByMonth: Record<string, number> = {};
  let owed = 0;
  for (const month of [...months].sort()) {
    const d = m[month];
    if (d) owed += d.taken - d.repaid; // borrowed adds, repaid subtracts
    owedByMonth[month] = Math.max(owed, 0);
  }

  return months
    .sort((a, b) => b.localeCompare(a)) // display newest first
    .map((month) => {
      const d = m[month] || { taken: 0, repaid: 0, mortgage: 0, afterpay: 0, overdraft: 0 };
      const income = incomeByMonth[month] || 0;
      // % of income to debt = Cash App Borrow REPAID only. The Weokie mortgage is
      // EXCLUDED — it's a mortgage (a different kind of debt), shown in its own
      // column but not lumped into this Cash-App-borrowing ratio. (#131)
      return {
        month,
        income,
        borrowTaken: d.taken,
        borrowRepaid: d.repaid,
        borrowCost: d.repaid - d.taken,
        mortgagePaid: d.mortgage,
        afterpayOut: d.afterpay,
        overdraftOut: d.overdraft,
        owedEnd: owedByMonth[month] ?? 0,
        debtToIncomePct: income > 0 ? (d.repaid / income) * 100 : 0,
      };
    });
}

// ─── Loan story series: balance + cost over time (#131 story redesign) ──

export interface LoanSeriesPoint {
  month: string;     // "YYYY-MM"
  label: string;     // "Apr 2026"
  owed: number;      // running Cash App Borrow balance owed at end of month (>=0)
  cost: number;      // directional cost that month (repaid - borrowed; >=0 shown)
  borrowed: number;
  repaid: number;
}

function labelFor(month: string): string {
  const [y, m] = month.split("-");
  return new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

/**
 * Per-month Cash App Borrow series for the story charts — OLDEST→NEWEST so the
 * charts read left-to-right in time. Running `owed` (what's carried) and `cost`
 * (fees that month). Pure; built from the same rows as everything else.
 */
export function getCashAppBorrowSeries(rows: CreditRow[]): LoanSeriesPoint[] {
  const byMonth: Record<string, { borrowed: number; repaid: number }> = {};
  for (const r of rows) {
    if (!isCashAppBorrow(r)) continue;
    const k = monthKey(r.date);
    if (!k) continue;
    const b = (byMonth[k] ??= { borrowed: 0, repaid: 0 });
    const a = signedAmount(r);
    if (a > 0) b.borrowed += a;
    else b.repaid += -a;
  }
  let owed = 0;
  return Object.keys(byMonth)
    .sort() // oldest first
    .map((month) => {
      const d = byMonth[month];
      owed += d.borrowed - d.repaid;
      return {
        month,
        label: labelFor(month),
        owed: Math.max(owed, 0),
        cost: Math.max(d.repaid - d.borrowed, 0),
        borrowed: d.borrowed,
        repaid: d.repaid,
      };
    });
}

// ─── Month drill-down: real Cash App Borrow transactions (#131 Phase 4) ──

export interface BorrowTxn {
  date: string;
  signed: number;        // + borrowed, - repaid
  kind: "borrowed" | "repaid";
  runningOwed: number;   // running balance WITHIN the month (real, no modeling)
}

/**
 * Every Cash App Borrow transaction in one month, with a running balance — 100%
 * real rows, no modeling. Lets a user click a month and SEE exactly what happened:
 * each disbursement (+) and repayment (-), in order, and how the balance moved.
 * `monthKey` is "YYYY-MM".
 */
export function getMonthBorrowDetail(rows: CreditRow[], month: string): BorrowTxn[] {
  const inMonth = rows
    .filter((r) => isCashAppBorrow(r) && monthKey(r.date) === month)
    .sort((a, b) => a.date.localeCompare(b.date));
  let owed = 0;
  return inMonth.map((r) => {
    const signed = signedAmount(r);
    owed += signed;
    return {
      date: r.date,
      signed,
      kind: signed > 0 ? "borrowed" : "repaid",
      runningOwed: owed,
    };
  });
}

// ─── Product registry + adaptive detection (#131 Phase 3) ───────────────

/** How a product's COST should be read (drives what the UI claims). */
export type CostBasis =
  | "proven"      // cost = repaid - borrowed, provable (Cash App Borrow)
  | "cashflow"    // money out is real, cost not derivable here (Afterpay)
  | "zero-fee"    // documented fee-free (Overdraft)
  | "paid-only";  // total paid; principal/interest split needs the rate (mortgage)

/** Top-level grouping for the loans page sections. */
export type LoanGroup = "mortgage" | "cashapp" | "other";

export interface ProductMeta {
  product: CreditProduct;
  group: LoanGroup;
  displayName: string;
  costBasis: CostBasis;
  /** Plain-English how-it-works; for Cash App, sourced from cash.app/legal. */
  explainer?: string;
  sourceUrl?: string;
}

/** The known products, with how to present each. "other" is the generic fallback. */
export const PRODUCT_REGISTRY: Record<Exclude<CreditProduct, "other">, ProductMeta> = {
  "cashapp-borrow": {
    product: "cashapp-borrow",
    group: "cashapp",
    displayName: "Cash App Borrow",
    costBasis: "proven",
    explainer:
      "A short-term loan from Cash App. The finance charge is ~5% of what you borrow, " +
      "withheld up front. If a balance isn't repaid by its ~4-week due date, overdue " +
      "interest of 1.25%/week is added, plus a $5 late fee. Your cost = total repaid − " +
      "total borrowed.",
    sourceUrl: "https://cash.app/legal/us/en-us/borrow-loan-agreement",
  },
  "weokie-mortgage": {
    product: "weokie-mortgage",
    group: "mortgage",
    displayName: "Weokie Mortgage",
    costBasis: "paid-only",
    explainer:
      "An external mortgage paid through Cash App. Each payment is part principal " +
      "(builds equity) and part interest (the real cost). Splitting the two needs the " +
      "loan's interest rate — add it to see interest vs principal.",
  },
  overdraft: {
    product: "overdraft",
    group: "cashapp",
    displayName: "Overdraft Coverage",
    costBasis: "zero-fee",
    explainer:
      "Cash App covers a small negative balance and claws it back later. Per Cash App's " +
      "policy this coverage is fee-free, so it costs $0 — the money out is just repaying " +
      "what was spent, not a fee.",
  },
  afterpay: {
    product: "afterpay",
    group: "cashapp",
    displayName: "Afterpay (installments)",
    costBasis: "cashflow",
    explainer:
      "Buy Now, Pay Later: a purchase split into installments. The purchase is funded at " +
      "the store (not in Cash App), so only the installment payments show here — the true " +
      "cost (any late fees) isn't in this data. Import an Afterpay statement to see fees.",
  },
};

export interface DetectedLoan extends ProductMeta {
  rows: number;
  paidOut: number;     // total money out (debits)
  takenIn: number;     // total money in (credits) — disbursements
  /** Provable cost when costBasis==="proven", else null. */
  cost: number | null;
}

/**
 * Detect which loan/credit products THIS user actually has, from their
 * transactions. Returns only products present (empty array → the page shows an
 * empty state). Generic "other" loans aren't auto-grouped here yet — known
 * products only — so the page adapts per user without inventing categories.
 */
export function detectUserLoans(rows: CreditRow[]): DetectedLoan[] {
  const byProduct = new Map<CreditProduct, CreditRow[]>();
  for (const r of rows) {
    const p = classifyCreditProduct(r);
    if (p === "other") continue;
    let bucket = byProduct.get(p);
    if (!bucket) {
      bucket = [];
      byProduct.set(p, bucket);
    }
    bucket.push(r);
  }
  const out: DetectedLoan[] = [];
  for (const [product, prodRows] of byProduct) {
    const meta = PRODUCT_REGISTRY[product as Exclude<CreditProduct, "other">];
    let takenIn = 0;
    let paidOut = 0;
    for (const r of prodRows) {
      const a = signedAmount(r);
      if (a > 0) takenIn += a;
      else paidOut += -a;
    }
    const cost = meta.costBasis === "proven" ? Math.max(paidOut - takenIn, 0) : null;
    out.push({ ...meta, rows: prodRows.length, takenIn, paidOut, cost });
  }
  // Stable order: mortgage first, then cashapp products, by display name.
  const groupOrder: Record<LoanGroup, number> = { mortgage: 0, cashapp: 1, other: 2 };
  return out.sort(
    (a, b) => groupOrder[a.group] - groupOrder[b.group] || a.displayName.localeCompare(b.displayName)
  );
}
